import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'src/hooks';
import { Deal } from 'src/types';
import { getRootUrl } from '@share-a-cart/shared-utils';
import { Link } from 'react-router-dom';
import { DealExpiryPill } from './DealExpiryPill';
import { dealsPath, expiryCountdown, startedWithinDay, storeSlug } from './dealFilters';
import { NewDealBadge } from './NewDealBadge';

type Props = {
  deal: Deal;
  path: string;
  /** Show the full title and description instead of clamping to two lines. */
  expanded?: boolean;
};

export const DealCard = ({ deal, path, expanded = false }: Props) => {
  const { t } = useTranslation();
  const copyToClipboard = useCopyToClipboard();
  const hasExpiry = expiryCountdown(deal.expires_at) != null;
  const isNew = startedWithinDay(deal.effective_at);
  const dealClickHref = `${deal.dealClickBase}/web`;
  const shareUrl = `${getRootUrl()}${path}`;
  const storeLabel = deal.retailerDisplayName || deal.retailer;
  const storePath = dealsPath(storeSlug(deal));
  const storeLinkClass = 'transition-opacity hover:opacity-80';

  const handleCopyLink = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'Deals',
        eventCategory: 'engagement',
        eventLabel: 'Deal Link Copied',
        eventValue: {
          dealName: deal.name,
          vendor: deal.retailer,
        },
      },
    });
    copyToClipboard(shareUrl, { content: t('deals.link_copied') });
  };

  return (
    <div className='flex h-full min-w-0 flex-col gap-3 overflow-hidden rounded-xl bg-[#FBE9BA] p-4'>
      <div className='flex min-w-0 items-start gap-3'>
        <Link
          to={storePath}
          aria-label={t('deals.store_link', { store: storeLabel })}
          className={`flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white ${storeLinkClass}`}
        >
          {deal.image_url ? (
            <img src={deal.image_url} alt='' className='h-9 w-9 object-contain' />
          ) : (
            <span aria-hidden className='text-lg font-bold text-primary-dark'>
              {storeLabel.charAt(0).toUpperCase()}
            </span>
          )}
        </Link>

        <div className='flex min-w-0 flex-1 flex-col gap-1'>
          <div className='flex flex-wrap items-center gap-2'>
            {deal.retailerDisplayName && (
              <Link
                to={storePath}
                className={`font-poppins text-[11px] font-semibold uppercase tracking-widest text-[#8b6914] ${storeLinkClass}`}
              >
                {deal.retailerDisplayName}
              </Link>
            )}
            {isNew && <NewDealBadge />}
          </div>
          <h3 className={`heading-200 !text-[#302404] ${expanded ? '' : 'line-clamp-2'}`}>
            <Link to={path} className='transition-opacity hover:opacity-80'>
              {deal.name}
            </Link>
          </h3>
        </div>
      </div>

      {deal.description && (
        <p className={`text-400 !text-[#3c2c04] ${expanded ? '' : 'line-clamp-2'}`}>
          {deal.description}
        </p>
      )}

      <div className='mt-auto flex flex-col gap-3'>
        {(deal.code || hasExpiry) && (
          <div className='flex flex-wrap items-center gap-2'>
            {deal.code && (
              <p className='font-openSans text-xs text-[#3c2c04]'>
                Code: <span className='font-semibold'>{deal.code}</span>
              </p>
            )}
            <DealExpiryPill expiresAt={deal.expires_at} />
          </div>
        )}

        <div className='flex gap-2'>
          <a
            href={dealClickHref}
            target='_blank'
            rel='sponsored nofollow noopener noreferrer'
            className='button-primary flex h-10 min-w-0 flex-1 items-center gap-1.5 !px-3'
            onClick={() => {
              TagManager.dataLayer({
                dataLayer: {
                  event: 'Deals',
                  eventCategory: 'engagement',
                  eventLabel: 'Deal Clicked',
                  eventValue: {
                    dealName: deal.name,
                    vendor: deal.retailer,
                    dealLink: deal.link,
                  },
                },
              });
            }}
          >
            Get Offer
            <ArrowForwardIcon style={{ fontSize: '16px' }} />
          </a>
          <button
            type='button'
            onClick={handleCopyLink}
            aria-label={t('common.copy_link')}
            className='button-secondary flex h-10 w-10 shrink-0 items-center justify-center !px-0 hover:!bg-[#f6d06d]'
          >
            <ContentCopyIcon style={{ fontSize: '18px' }} />
          </button>
        </div>
      </div>
    </div>
  );
};
